import { useTranslation } from 'react-i18next'
import { Button, SettingsGroup, PageHeader } from '@airclou/ui'
import { Mail, FileText, ExternalLink } from 'lucide-react'
import { SUPPORT_EMAIL, DOCUMENTATION_URL } from '../../config'

/**
 * Destination for the profile menu's "Help & Support" entry.
 *
 * The menu item has pointed at `/support` since it was added, but no route was
 * ever registered — the catch-all silently rendered Overview instead, so the
 * link looked like it worked.
 *
 * Deliberately NOT built on `@airclou/ui`'s `HelpSupportPanel` (which Helpdesk
 * uses): every string in that component is hardcoded English, and this app ships
 * a full zh translation. Using it would leave a zh operator reading a translated
 * heading over an English body.
 *
 * Every row here performs a real action. The previous version of this page —
 * built but never routed — had a "Start Chat" button with no handler, four
 * knowledgebase cards styled `cursor-pointer` with no `onClick`, and a contact
 * form whose submit button discarded what you typed. Dead controls are worse
 * than absent ones, so they are gone rather than wired to nothing. Their strings
 * went with them, rather than sitting unused in two locale files.
 *
 * The second row is **Documentation**, not "Knowledgebase". Those are different
 * things here: documentation is the product manual at `airclou.com/docs` (MDX in
 * `apps/hub`), the knowledgebase is the customer-facing help center at
 * `hc.airclou.com`. See `apps/helpdesk/knowledge/content-architecture.md`.
 */
export function SupportPage() {
  const { t } = useTranslation('dashboard')

  const emailSupport = () => {
    // A subject the recipient can triage. The page title alone ("Support")
    // says nothing a support inbox doesn't already know.
    const subject = encodeURIComponent(t('support.emailSubject'))
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('support.title')} description={t('support.subtitle')} />

      <div className="max-w-2xl space-y-4">
        {/* "Support Resources", not "Contact Us": one row is contact, the
            other is a link to the manual. */}
        <h2 className="text-lg font-semibold text-foreground">{t('support.resources')}</h2>
        <SettingsGroup
          sections={[
            {
              icon: Mail,
              title: t('support.emailSupport'),
              description: t('support.emailSupportDesc'),
              action: (
                <Button variant="outline" size="sm" onClick={emailSupport}>
                  {t('support.sendEmail')}
                </Button>
              ),
            },
            {
              icon: FileText,
              title: t('support.documentation'),
              description: t('support.documentationDesc'),
              action: (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => window.open(DOCUMENTATION_URL, '_blank', 'noopener,noreferrer')}
                >
                  {t('support.viewDocs')}
                  <ExternalLink className="h-3 w-3" />
                </Button>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}
